// src/routes/statistics/report/components/DownloadSection.js

import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import Switch from 'react-switch';
import { ko } from 'date-fns/locale';
import './DownloadSection.css';

const HELP_ITEMS = [
  '자동 다운로드를 켜두면 매일 설정한 시간에 발전 데이터 CSV 파일이 저장됩니다.',
  '설정한 시간과 사용 여부는 현재 사용 중인 기기와 브라우저에 저장됩니다.',
  '브라우저 설정, 캐시 삭제, 사용 환경에 따라 설정 시간이 초기화되거나 이전 값으로 돌아갈 수 있습니다.',
  'CSV 파일이 자동으로 저장되지 않는 경우, 상단의 설명서 버튼을 눌러 다운로드한 뒤 브라우저 다운로드 설정을 확인해 주세요.',
  '필요한 날짜의 데이터를 바로 받고 싶을 때는 수동 다운로드를 사용하세요.',
];

const DownloadSection = ({
  autoDownloadEnabled,
  setAutoDownloadEnabled,
  autoDownloadTime,
  setAutoDownloadTime,
  manualDownloadDate,
  setManualDownloadDate,
  handleManualDownload,
  isDownloading,
}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  return (
    <section className="download-section" aria-label="CSV 다운로드 설정">
      <div className="download-section__panel">
        <header className="download-section__header">
          <div className="download-section__title-block">
            <h2 className="download-section__title">발전 데이터 CSV</h2>
            <p className="download-section__subtitle">
              자동·수동으로 일별 발전 리포트를 내려받을 수 있습니다.
            </p>
          </div>

          <button
            type="button"
            className={`download-section__help-btn ${
              isHelpOpen ? 'download-section__help-btn--open' : ''
            }`}
            onClick={() => setIsHelpOpen((prev) => !prev)}
            aria-expanded={isHelpOpen}
            aria-controls="download-section-help"
          >
            {isHelpOpen ? '도움말 닫기' : '도움말'}
          </button>
        </header>

        {isHelpOpen && (
          <div
            id="download-section-help"
            className="download-section__help-panel"
            role="region"
            aria-label="사용 안내"
          >
            <ul>
              {HELP_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="download-section__grid">
          <article className="download-card">
            <div className="download-card__top">
              <div className="download-card__icon-wrap download-card__icon-wrap--auto" aria-hidden="true">
                ⏱
              </div>

              <div className="download-card__text">
                <div className="download-card__title-row">
                  <span className="download-card__name">자동 다운로드</span>
                  <span
                    className={`download-card__status ${
                      autoDownloadEnabled
                        ? 'download-card__status--on'
                        : 'download-card__status--off'
                    }`}
                  >
                    {autoDownloadEnabled ? '사용 중' : '꺼짐'}
                  </span>
                </div>

                <span className="download-card__desc">
                  매일 지정 시각에 당일 데이터 CSV를 저장합니다.
                </span>
              </div>
            </div>

            <div className="download-card__body">
              <div className="download-card__setting-row">
                <span className="download-card__label">자동 다운로드</span>

                <Switch
                  onChange={() => setAutoDownloadEnabled(!autoDownloadEnabled)}
                  checked={autoDownloadEnabled}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  onColor="#93c5fd"
                  offColor="#d9dee7"
                  onHandleColor="#2563eb"
                  offHandleColor="#8b95a5"
                  height={22}
                  width={44}
                  aria-label="자동 다운로드 사용"
                />
              </div>

              <div
                className={`download-card__setting-row ${
                  !autoDownloadEnabled ? 'download-card__setting-row--disabled' : ''
                }`}
              >
                <label className="download-card__label" htmlFor="auto-download-time">
                  실행 시간
                </label>

                <input
                  id="auto-download-time"
                  type="time"
                  className="download-card__time-input"
                  value={autoDownloadTime}
                  onChange={(e) => setAutoDownloadTime(e.target.value)}
                  disabled={!autoDownloadEnabled}
                />
              </div>
            </div>

            <p
              className={`download-card__notice ${
                autoDownloadEnabled
                  ? 'download-card__notice--active'
                  : 'download-card__notice--inactive'
              }`}
            >
              {autoDownloadEnabled ? (
                <>
                  <strong>매일 {autoDownloadTime}</strong>에{' '}
                  <code>태양광발전일지_개별_날짜.csv</code> 파일이 자동 저장됩니다.
                </>
              ) : (
                <>스위치를 켜면 실행 시간을 설정할 수 있습니다.</>
              )}
            </p>
          </article>

          <article className="download-card">
            <div className="download-card__top">
              <div className="download-card__icon-wrap download-card__icon-wrap--manual" aria-hidden="true">
                ↓
              </div>

              <div className="download-card__text">
                <div className="download-card__title-row">
                  <span className="download-card__name">수동 다운로드</span>
                </div>

                <span className="download-card__desc">
                  원하는 날짜를 선택해 바로 CSV를 받습니다.
                </span>
              </div>
            </div>

            <div className="download-card__manual-box">
              <div className="download-card__date-row">
                <label className="download-card__label" htmlFor="manual-download-date">
                  조회 날짜
                </label>

                <DatePicker
                  id="manual-download-date"
                  selected={manualDownloadDate}
                  onChange={(date) => setManualDownloadDate(date)}
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                  locale={ko}
                  customInput={<input className="date-input" readOnly />}
                />
              </div>

              <button
                type="button"
                className="download-card__btn download-card__btn--primary"
                onClick={handleManualDownload}
                disabled={isDownloading}
              >
                {isDownloading && (
                  <span className="download-card__spinner" aria-hidden="true" />
                )}
                {isDownloading ? '다운로드 중…' : 'CSV 다운로드'}
              </button>
            </div>

            <p className="download-card__notice download-card__notice--manual">
              선택한 날짜 기준으로 일별 발전 데이터 CSV를 생성합니다.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;